/** Sample-ID rules (PRD §9.3). The verbatim value is always stored beside the normalized one. */

/** Longer than any real lab label; also bounds what a (safe) pattern can be asked to scan. */
export const MAX_SAMPLE_ID_LENGTH = 64;
export const MAX_LABEL_PATTERN_LENGTH = 200;
/**
 * A choice point is anywhere the engine can try more than one way forward: a variable-width
 * quantifier, or a group (or the top level) containing alternation. Backtracking multiplies across
 * choice points, so chains of them — `\d+\d+…`, `a?a?…`, `(a|a)(a|a)…` — explode even with no
 * nesting. Capping the total keeps the worst case polynomial (≈ n^3 per start) on a 64-char id.
 */
export const MAX_CHOICE_POINTS = 3;

/**
 * NFKC first, so full-width and compatibility forms fold to ASCII instead of being stripped (a
 * pasted 'Ｈ１２３' is 'H123', not null). Look-alike misreads (O/0, I/1) are deliberately NOT
 * folded: real labels use both letters and digits, so folding would merge distinct bottles.
 */
export function normalizeSampleId(raw: string | null | undefined): string | null {
  const v = String(raw ?? '').normalize('NFKC').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return v.length > 0 ? v : null;
}

const QUANTIFIER_BRACE = /^\{(\d+)(,(\d*))?\}/;

/** `{n}` and `{n,n}` match a fixed width; every other quantifier is variable. */
const braceIsVariable = (b: RegExpExecArray): boolean => b[2] !== undefined && b[3] !== b[1];

/** Length of the group modifier after "(", e.g. "?:" = 2, "?<=" = 3, "?<name>" = 7. */
function groupModifierLength(p: string, i: number): number {
  if (p[i] !== '?') return 0;
  const next = p[i + 1];
  if (next === ':' || next === '=' || next === '!') return 2;
  if (next === '<' && (p[i + 2] === '=' || p[i + 2] === '!')) return 3;
  if (next === '<') {
    const close = p.indexOf('>', i);
    return close === -1 ? 1 : close - i + 1;
  }
  return 1;
}

/**
 * Save-time gate for a lab's label pattern (PRD §7.4a). A lab label pattern is admin-entered text
 * that runs against every scanned bottle, so a pattern that can backtrack catastrophically — e.g.
 * `^(A+)+$` — would hang the intake desk. Conservative on purpose: it rejects any quantified group
 * that itself contains a quantifier or an alternation, any backreference, and more than
 * MAX_CHOICE_POINTS choice points in total (see above). Real label shapes (`^H\d{3}-\d{5}-\d{4}$`,
 * `^(59CS|HT)\d+$`, `^59CS-?\d{5}$`) need at most two.
 */
export function isSafeLabelPattern(pattern: string): boolean {
  if (pattern.length > MAX_LABEL_PATTERN_LENGTH) return false;
  try {
    new RegExp(pattern);
  } catch {
    return false;
  }
  // A scanner's working stack (local, never escapes): one entry per open group — does it
  // (transitively) contain a quantifier or an alternation?
  const risky: boolean[] = [false];
  const hasAlternation: boolean[] = [false]; // per open group: a DIRECT '|' (each such group = 1 choice point)
  const markTop = (): void => {
    risky[risky.length - 1] = true;
  };
  let closedGroupRisky: boolean | null = null; // the atom just before this char was a group
  let afterQuantifier = false;
  let choicePoints = 0;
  let i = 0;
  while (i < pattern.length) {
    const c = pattern[i];
    const brace = c === '{' ? QUANTIFIER_BRACE.exec(pattern.slice(i)) : null;
    if (c === '\\') {
      if (/[1-9k]/.test(pattern[i + 1] ?? '')) return false; // backreference
      i += 2;
    } else if (c === '[') {
      i += pattern[i + 1] === '^' ? 2 : 1;
      if (pattern[i] === ']') i += 1;
      while (i < pattern.length && pattern[i] !== ']') i += pattern[i] === '\\' ? 2 : 1;
      i += 1;
    } else if (c === '(') {
      risky.push(false);
      hasAlternation.push(false);
      i += 1 + groupModifierLength(pattern, i + 1);
    } else if (c === ')') {
      const groupRisky = risky.pop() ?? false;
      if (groupRisky) markTop();
      if (hasAlternation.pop()) choicePoints += 1;
      if (choicePoints > MAX_CHOICE_POINTS) return false;
      i += 1;
      closedGroupRisky = groupRisky;
      afterQuantifier = false;
      continue;
    } else if (c === '|') {
      markTop();
      hasAlternation[hasAlternation.length - 1] = true;
      i += 1;
    } else if (c === '*' || c === '+' || c === '?' || brace) {
      if (!(c === '?' && afterQuantifier)) {
        if (closedGroupRisky) return false;
        if (!brace || braceIsVariable(brace)) choicePoints += 1;
        if (choicePoints > MAX_CHOICE_POINTS) return false;
        markTop();
      }
      i += brace ? brace[0].length : 1;
      closedGroupRisky = null;
      afterQuantifier = !(c === '?' && afterQuantifier);
      continue;
    } else {
      i += 1;
    }
    closedGroupRisky = null;
    afterQuantifier = false;
  }
  return choicePoints + (hasAlternation[0] ? 1 : 0) <= MAX_CHOICE_POINTS;
}

/**
 * Match a scanned id against a lab's label pattern. Defence in depth: the pattern is re-checked for
 * safety here even though the lab-profile save rejects unsafe ones, and an over-long id is refused
 * before any pattern runs on it. A label check is a hint for the office, never an authorization signal.
 */
export function labelMatchesPattern(sampleId: string, pattern: string): 'match' | 'mismatch' | 'no_pattern' | 'bad_pattern' {
  if (pattern.trim() === '') return 'no_pattern';
  if (!isSafeLabelPattern(pattern)) return 'bad_pattern';
  const id = sampleId.trim();
  if (id.length > MAX_SAMPLE_ID_LENGTH) return 'mismatch';
  return new RegExp(pattern).test(id) ? 'match' : 'mismatch';
}
